import React, { useCallback, useEffect, useState } from "react";
import { Labeled, useGetList, useNotify, useRecordContext, useRefresh } from "react-admin";
import { Box, Checkbox, FormGroup, FormControlLabel, TextField as TextInput } from "@mui/material";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { JOBS } from "utils/constants";
import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import { careerCenterRequests } from "api/careerCenterApi";
import dataProvider from "server/dataProvider";

const stringData = record =>
  record?.map(item => ({
    id: item.name,
    value: item?.currentValue,
    label: item.label,
  }));

const ReturnCorrectionModal = ({ correctionsUrl, type }) => {
  const record = useRecordContext();
  const correctionId = record?.id;

  const { data } = useGetList(correctionsUrl, {}, { enabled: !!correctionId });
  const isJobs = type === JOBS;
  const [processedData, setProcessedData] = useState([]);
  const [message, setMessage] = useState(null);
  const [selectedChoices, setSelectedChoices] = useState([]);

  const notify = useNotify();
  const refresh = useRefresh();

  const fetchData = useCallback(async () => {
    try {
      const settlementIds = data
        .filter(item => item?.name === "SettlementId")
        .map(item => item.currentValue)
        .filter(Boolean);

      const [
        offerTypesRes,
        categoryTypesRes,
        employmentTypesRes,
        contractTypesRes,
        applicationMethodsRes,
        settlementsRes,
        candidateTypesRes,
      ] = await Promise.all([
        dataProvider.customRequest(careerCenterRequests.getJobOfferTypesSelect()),
        dataProvider.customRequest(careerCenterRequests.getJobCategoryTypesSelect()),
        dataProvider.customRequest(careerCenterRequests.getEmploymentTypesSelect()),
        dataProvider.customRequest(careerCenterRequests.getContractTypesSelect()),
        dataProvider.customRequest(careerCenterRequests.getJobApplicationMethodsSelect()),
        settlementIds.length
          ? dataProvider.customRequest(administrationRequests.getSettlementsByIdsSelect(settlementIds))
          : Promise.resolve({ data: [] }),
        dataProvider.customRequest(careerCenterRequests.getJobOffersCandidateTypesSelect()),
      ]);

      const updatedData = data.map(item => {
        switch (item?.name) {
          case "Type": {
            const match = offerTypesRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          case "Category": {
            const match = categoryTypesRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          case "EmploymentType": {
            const match = employmentTypesRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          case "ContractType": {
            const match = contractTypesRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          case "ApplicationMethods": {
            const match = applicationMethodsRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          case "SettlementId": {
            const match = settlementsRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          case "CandidateTypes": {
            const match = candidateTypesRes.data.find(el => el?.id === +item.currentValue);
            return { ...item, currentValue: match?.nameBg };
          }

          default:
            return item;
        }
      });

      setProcessedData(updatedData);
    } catch (error) {
      console.error(error);
    }
  }, [data]);

  useEffect(() => {
    if (!data) return;
    if (isJobs) fetchData();
  }, [data, isJobs, fetchData]);

  const handleConfirm = async () => {
    await dataProvider.customRequest(correctionsUrl, { fields: selectedChoices, comment: message }, "POST");
    notify("Успешно изпратена заявка за корекция.", { type: "success" });
    refresh();
  };

  const handleChange = id => {
    setSelectedChoices(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
  };

  const resetState = () => {
    setSelectedChoices([]);
    setMessage(null);
  };

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      pb={1}
    >
      <ConfirmModal
        titleButton="Върни за корекция"
        startIcon={<ModeEditIcon />}
        titleContent="Моля отбележете полетата които да бъдат върнати за корекция"
        saveTitle="Изпрати"
        closeTitle="Откажи"
        onConfirm={handleConfirm}
        resetProps={resetState}
        confirmEnabled={selectedChoices.length > 0}
        dialogProps={{
          sx: {
            "& .MuiDialog-paper": {
              maxHeight: "80%",
              minWidth: "50%",
            },
            "& .MuiDialogContent-root": {
              overflowX: "hidden !important",
            },
          },
        }}
      >
        <FormGroup
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "1rem",
            marginBottom: "1.5em",
            ...settings.style.shadowBox,
            maxWidth: "none", // shadowBox's cap assumes its 2rem side margins; the left one is dropped here
            marginLeft: 0,
          }}
        >
          {stringData(isJobs ? processedData : data)?.map(item =>
            item.value ? (
              <Labeled
                key={item.id}
                label={item.label}
                sx={{ textAlign: "left" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedChoices.includes(item.id)}
                      onChange={() => handleChange(item.id)}
                    />
                  }
                  label={` ${item.value}`}
                />
              </Labeled>
            ) : null,
          )}
        </FormGroup>

        <TextInput
          label="Въведи съобщение"
          multiline
          variant="outlined"
          onChange={e => setMessage(e.target.value)}
          maxRows={6}
          minRows={4}
          fullWidth
        />
      </ConfirmModal>
    </Box>
  );
};

export default ReturnCorrectionModal;
